import React, { useEffect, useMemo, useState } from 'react'
import {
    Card,
    CardContent,
    Button,
    CircularProgress,
    TextField,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { Alert } from '@material-ui/lab'
import Moment from 'react-moment'
import ReactJson from 'react-json-view'
import {
    adminSearchReceivedHooks,
    adminGetReceivedHook,
} from 'actions/applicants'
import { getClientsList } from 'actions/client'
import { Toast } from 'components/shared/Toaster'

const SERVICES = ['PLAID', 'FCS', 'COBALT', 'DOCUSIGN', 'STRIPE']
const STATUSES = ['RECEIVED', 'PRIORITY_RECEIVED', 'PROCESSING', 'FINISHED', 'ERROR', 'DUPLICATED', 'IGNORED']
// Type enum is very large; a text field with a curated dropdown of common values is enough.
const COMMON_TYPES = [
    'FILING_STATUS',
    'SOS_SEARCH_RESULTS', 'SOS_SEARCH_RESULTS_AUTO_REPORT', 'SOS_SEARCH_RESULTS_MONITORING', 'SOS_ALL_STATES_RESULT',
    'DOCUSIGN_CHECK_ENVELOPE_STATUS',
    'INVOICE_PAID', 'INVOICE_PAYMENT_FAILED', 'CUSTOMER_SUBSCRIPTION_CREATED', 'CUSTOMER_SUBSCRIPTION_DELETED',
    'UNKNOWN', 'IGNORED',
]
const PAGE_SIZES = [10, 20, 50, 100]

const statusChipStyle = (status) => {
    switch (status) {
        case 'FINISHED': return { background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }
        case 'ERROR': return { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }
        case 'PROCESSING':
        case 'RECEIVED':
        case 'PRIORITY_RECEIVED': return { background: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }
        case 'DUPLICATED':
        case 'IGNORED': return { background: 'var(--badge-watch-bg)', color: '#e65100' }
        default: return { background: 'var(--bg-canvas)', color: '#555' }
    }
}

const COLUMN_DEFS = [
    { key: 'createdAt', header: 'Created', get: (r) => r.createdAt },
    { key: 'service', header: 'Service', get: (r) => r.service },
    { key: 'type', header: 'Type', get: (r) => r.type },
    { key: 'status', header: 'Status', get: (r) => r.status },
    { key: 'client', header: 'Client', get: (r) => r.clientName || r.clientId },
    { key: 'idempotencyKey', header: 'Idempotency Key', get: (r) => r.idempotencyKey, mono: true },
    { key: 'uuid', header: 'UUID', get: (r) => r.uuid, mono: true },
]

const isEmpty = (v) => v == null || v === '' || v === '—'

// Payloads arrive as JSON strings — try to parse for the pretty tree viewer, fall back to raw text.
const parsePayload = (raw) => {
    if (raw == null || raw === '') return null
    if (typeof raw !== 'string') return raw
    try { return JSON.parse(raw) } catch { return raw }
}

const ReceivedHooks = () => {
    const [clients, setClients] = useState([])
    const [clientsLoading, setClientsLoading] = useState(false)
    const [params, setParams] = useState({
        service: '',
        type: '',
        status: '',
        clientId: '',
        idempotencyKey: '',
        uuid: '',
    })
    const [pageLimit, setPageLimit] = useState(20)
    const [pageNumber, setPageNumber] = useState(0)
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [detail, setDetail] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)

    useEffect(() => {
        setClientsLoading(true)
        getClientsList({}, {}, true)(() => {})
            .then((res) => {
                const list = res?.response || res?.responseBody || res || []
                setClients(Array.isArray(list) ? list : [])
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load clients' }))
            .finally(() => setClientsLoading(false))
    }, [])

    const setParam = (key) => (e) => setParams((p) => ({ ...p, [key]: e.target.value }))

    const runSearch = (page = 0) => {
        setLoading(true)
        setHasSearched(true)
        setPageNumber(page)
        adminSearchReceivedHooks({ params, pageLimit, pageNumber: page })()
            .then((res) => {
                const list = res?.response || res?.responseBody || []
                setResults(Array.isArray(list) ? list : [])
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load received webhooks' }))
            .finally(() => setLoading(false))
    }

    const openDetail = (uuid) => {
        if (!uuid) return
        setDetail({ uuid, data: null })
        setDetailLoading(true)
        adminGetReceivedHook({ uuid })()
            .then((res) => setDetail({ uuid, data: res }))
            .catch(() => {
                Toast({ type: 'error', message: 'Failed to load webhook' })
                setDetail(null)
            })
            .finally(() => setDetailLoading(false))
    }

    const visibleColumns = useMemo(() => {
        if (!results.length) return COLUMN_DEFS
        return COLUMN_DEFS.filter((c) => results.some((r) => !isEmpty(c.get(r))))
    }, [results])

    const parsedPayload = useMemo(() => parsePayload(detail?.data?.payload), [detail])

    const copyPayload = () => {
        if (!detail?.data?.payload) return
        navigator.clipboard.writeText(detail.data.payload)
            .then(() => Toast({ type: 'success', message: 'Payload copied' }))
            .catch(() => Toast({ type: 'error', message: 'Copy failed' }))
    }

    const cellStyle = { padding: '8px 10px', borderBottom: '1px solid #eee', fontSize: 13, verticalAlign: 'top' }
    const headStyle = { ...cellStyle, background: 'var(--bg-canvas)', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #ddd' }

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>Received Webhooks</h4>
            </div>

            <Card variant='outlined' className='mb-3'>
                <CardContent>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        <TextField
                            select
                            label='Client'
                            value={params.clientId}
                            onChange={setParam('clientId')}
                            size='small'
                            variant='outlined'
                            disabled={clientsLoading}
                            helperText={clientsLoading ? 'Loading clients…' : `${clients.length} clients`}
                        >
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {clients
                                .slice()
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map((c) => (
                                    <MenuItem key={c.id || c.uuid} value={String(c.id ?? c.uuid)}>
                                        {c.name || '—'} <span style={{ color: '#999', marginLeft: 6 }}>#{c.id ?? c.uuid}</span>
                                    </MenuItem>
                                ))}
                        </TextField>
                        <TextField select label='Service' value={params.service} onChange={setParam('service')} size='small' variant='outlined'>
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {SERVICES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                        <TextField select label='Status' value={params.status} onChange={setParam('status')} size='small' variant='outlined'>
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                        <TextField select label='Type' value={params.type} onChange={setParam('type')} size='small' variant='outlined'>
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {COMMON_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                        <TextField label='Idempotency Key' value={params.idempotencyKey} onChange={setParam('idempotencyKey')} size='small' variant='outlined' />
                        <TextField label='UUID' value={params.uuid} onChange={setParam('uuid')} size='small' variant='outlined' />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                        <TextField
                            select
                            label='Page size'
                            value={pageLimit}
                            onChange={(e) => setPageLimit(Number(e.target.value))}
                            size='small'
                            variant='outlined'
                            style={{ width: 120 }}
                        >
                            {PAGE_SIZES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                        <Button
                            startIcon={<SearchIcon />}
                            variant='contained'
                            color='primary'
                            onClick={() => runSearch(0)}
                            disabled={loading}
                        >
                            Search
                        </Button>
                        {loading && <CircularProgress size={20} />}
                    </div>
                </CardContent>
            </Card>

            {hasSearched && results.length === 0 && !loading && (
                <Alert severity='info'>No results. Adjust the filters and try again.</Alert>
            )}

            {results.length > 0 && (
                <Card variant='outlined'>
                    <CardContent>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {visibleColumns.map((c) => <th key={c.key} style={headStyle}>{c.header}</th>)}
                                        <th style={headStyle} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((row) => (
                                        <tr key={row.uuid}>
                                            {visibleColumns.map((c) => {
                                                const value = c.get(row)
                                                let rendered
                                                if (isEmpty(value)) rendered = <span style={{ color: '#bbb' }}>—</span>
                                                else if (c.key === 'createdAt') rendered = <Moment format='YYYY-MM-DD HH:mm:ss'>{value}</Moment>
                                                else if (c.key === 'status') rendered = <Chip label={value} size='small' style={statusChipStyle(value)} />
                                                else rendered = value
                                                return (
                                                    <td key={c.key} style={c.mono ? { ...cellStyle, fontFamily: 'monospace', fontSize: 11 } : cellStyle}>
                                                        {rendered}
                                                    </td>
                                                )
                                            })}
                                            <td style={cellStyle}>
                                                <Button size='small' onClick={() => openDetail(row.uuid)}>View</Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                            <span style={{ fontSize: 13, color: '#666' }}>
                                Page {pageNumber + 1} — showing {results.length}
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button size='small' variant='outlined' disabled={loading || pageNumber === 0} onClick={() => runSearch(Math.max(0, pageNumber - 1))}>
                                    Previous
                                </Button>
                                <Button size='small' variant='outlined' disabled={loading || results.length < pageLimit} onClick={() => runSearch(pageNumber + 1)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth='lg' fullWidth>
                <DialogTitle>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Received webhook</span>
                        <div>
                            <IconButton size='small' onClick={copyPayload} title='Copy payload'><FileCopyIcon fontSize='small' /></IconButton>
                            <IconButton size='small' onClick={() => setDetail(null)}><CloseIcon /></IconButton>
                        </div>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>{detail?.uuid}</div>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading && <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>}
                    {!detailLoading && detail?.data && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13, marginBottom: 16 }}>
                                <strong>Service:</strong><span>{detail.data.service || '—'}</span>
                                <strong>Type:</strong><span>{detail.data.type || '—'}</span>
                                <strong>Status:</strong><span>
                                    {detail.data.status ? <Chip label={detail.data.status} size='small' style={statusChipStyle(detail.data.status)} /> : '—'}
                                </span>
                                <strong>Client:</strong><span>{detail.data.clientName || detail.data.clientId || '—'}</span>
                                <strong>Idempotency:</strong><span style={{ fontFamily: 'monospace' }}>{detail.data.idempotencyKey || '—'}</span>
                                <strong>Received:</strong><span>{detail.data.createdAt ? <Moment format='YYYY-MM-DD HH:mm:ss'>{detail.data.createdAt}</Moment> : '—'}</span>
                                <strong>Started:</strong><span>{detail.data.startedAt ? <Moment format='YYYY-MM-DD HH:mm:ss'>{detail.data.startedAt}</Moment> : '—'}</span>
                                <strong>Finished:</strong><span>{detail.data.finishedAt ? <Moment format='YYYY-MM-DD HH:mm:ss'>{detail.data.finishedAt}</Moment> : '—'}</span>
                                {detail.data.errorMessage && (
                                    <>
                                        <strong style={{ color: 'var(--status-declined)' }}>Error:</strong>
                                        <span style={{ color: 'var(--status-declined)', wordBreak: 'break-word' }}>{detail.data.errorMessage}</span>
                                    </>
                                )}
                            </div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Payload</div>
                            {parsedPayload && typeof parsedPayload === 'object' ? (
                                <ReactJson
                                    src={parsedPayload}
                                    name={false}
                                    collapsed={2}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    style={{ fontSize: 12 }}
                                />
                            ) : (
                                <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'var(--bg-canvas)', padding: 12, borderRadius: 4 }}>
                                    {parsedPayload ?? '(empty)'}
                                </pre>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ReceivedHooks
